import React from "react";
import { Badge } from "../../ui/badge";
import { Card, CardContent } from "../../ui/card";
import { getStatusV, formatDuration } from "../../../utils/helper";
import { useSelector } from "react-redux";
import { getFeedbackForm, getInterviewResult } from "../../../redux/reducer/incbotFeedbackReducer";
import { CheckCircle, Clock } from "lucide-react";
import { QUESTION_TYPES } from "../../../constants";
import {
  collectInterviewWrittenTemplates,
  normalizeInterviewQuestionKey,
} from "./writtenQuestionUtils";

const SKIPPED_ANSWER_COPY = "The candidate skipped the question.";

/**
 * Rows: written prompts from the interview definition (`question.questions` / `interview.questions`),
 * merged with `feedbackForm.questions.data` by normalized question text. The block shows when
 * either side defines at least one written question.
 */

function isAnswerMissing(answer: string | undefined): boolean {
  const t = (answer ?? "").trim();
  return !t || t === "The candidate didn't answer.";
}

const WrittenSection = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const interview = useSelector(getInterviewResult);

  const writtenSectionData = React.useMemo(() => {
    const feedbackWritten = (feedbackForm?.questions?.data || []).filter((q: any) => {
      const qt = String(q?.questionType || "").toLowerCase();
      return qt === QUESTION_TYPES.WRITING;
    });

    const feedbackByKey = new Map<string, any>();
    for (const fq of feedbackWritten) {
      const key = normalizeInterviewQuestionKey(fq?.question || "");
      if (key) feedbackByKey.set(key, fq);
    }

    const templateWritten = collectInterviewWrittenTemplates(interview) as any[];
    const seenKeys = new Set<string>();
    const merged: any[] = [];

    const pushFromFeedback = (fb: any, templateQuestionText?: string) => {
      const displayQuestion = (fb?.question || templateQuestionText || "").trim();
      const skipped = isAnswerMissing(fb?.answer);
      merged.push({
        ...fb,
        question: displayQuestion || fb?.question,
        answer: skipped ? SKIPPED_ANSWER_COPY : fb.answer,
        rating: skipped ? 0 : (fb.rating ?? 0),
      });
    };

    for (const tq of templateWritten) {
      const qText = (tq?.translate?.question || tq?.question || "").trim();
      const key = normalizeInterviewQuestionKey(qText);
      if (!key) continue;
      seenKeys.add(key);
      const fb = feedbackByKey.get(key);
      if (fb) {
        pushFromFeedback(fb, qText);
      } else {
        merged.push({
          question: qText,
          answer: SKIPPED_ANSWER_COPY,
          rating: 0,
          questionType: QUESTION_TYPES.WRITING,
          ratingReason: "",
        });
      }
    }

    for (const fb of feedbackWritten) {
      const key = normalizeInterviewQuestionKey(fb?.question || "");
      if (!key || seenKeys.has(key)) continue;
      seenKeys.add(key);
      pushFromFeedback(fb);
    }

    return merged;
  }, [interview, feedbackForm?.questions?.data]);

  if (writtenSectionData.length === 0) {
    return null;
  }

  return (
    <Card className="shadow-sm mb-3 rounded-[12px] bg-[#F7F9FA] mt-4">
      <div className="space-y-1 mb-3 bg-[#EFF6FF] p-3 rounded-t-[12px]">
        <h3 className="text-lg font-medium text-gray-900">Written Assessment</h3>
        <p className="text-sm text-grayCustom-400">
          Assessment of written communication, clarity of thought, and subject knowledge.
        </p>
      </div>

      {writtenSectionData.map((item: any, i: number) => (
        <Card
          key={item?._id ?? `${i}-${normalizeInterviewQuestionKey(String(item?.question ?? ""))}`}
          className="mt-4 mb-4 bg-white shadow-sm border border-gray-200 w-[97%] mx-auto"
        >
          <CardContent className="p-0">
            <div className="flex-1 rounded-2xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-1 text-sm font-medium text-foreground">
                <div className="flex items-center gap-2">
                  <span className="px-[10px] py-[5px] rounded-full bg-blue-50">{i + 1}</span>
                  <span className="text-[16px]">{item.question}</span>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <CheckCircle
                    className={`text-${getStatusV(item?.rating ?? 0).color}-500`}
                    size={18}
                  />
                  <Badge
                    variant="outline"
                    className={`text-${getStatusV(item?.rating ?? 0).color}-700 border-${
                      getStatusV(item?.rating ?? 0).color
                    }-200 bg-${getStatusV(item?.rating ?? 0).color}-50 text-xs lg:text-sm`}
                  >
                    {`${(item?.rating ?? 0) * 10}% Correct`}
                  </Badge>
                </div>
              </div>

              <div className="text-[14px] leading-[25px] text-[#09090B]">
                <span className="font-bold mt-1">
                  Candidate Response: <br />
                </span>
                <div
                  className={`p-2 text-sm leading-relaxed rounded-md bg-[#F7F9FA] text-muted-foreground whitespace-pre-wrap break-words [overflow-wrap:anywhere]`}
                >
                  {item.answer}
                </div>
              </div>

              <div className="mb-4 mt-2">
                <div className="flex items-center gap-2 text-xs md:text-sm font-medium text-gray-700 mb-2">
                  <span>Response Analysis:</span>
                  {(item.questionSubmitTimestamp || item.timeTaken) > 0 && (
                    <div className="flex items-center gap-1 text-gray-500 font-normal">
                      <Clock size={14} />
                      <span className="text-xs">
                        {formatDuration(item.questionSubmitTimestamp ?? item.timeTaken ?? 0)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="bg-green-50 border-l-4 border-green-400 p-3 text-xs md:text-sm text-gray-700">
                  {item?.ratingReason || "Not Available"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </Card>
  );
};

export default WrittenSection;
